<template>
  <Card class="form-stepper-card sm:mt-16 box-shadow-none sm:box-shadow-sm sm:p-24">
    <h1 class="text-h2 centered mt-32 mb-16">Let's find the sitters closest to you</h1>
    <p class="text-body centered mb-24">We have sitters in 96% of U.S. neighborhoods.</p>
    <form>
      <div class="mb-32">
        <TextField
          id="zipCode"
          :model-value="zipCode"
          label-name="ZIP code"
          name="postal-code"
          type="text"
          maxlength="5"
          pattern="[0-9]*"
          class="mb-24"
          inputmode="numeric"
          :aria-label="errors.zipCode || 'Five-digit zip code'"
          :status="helperStatus"
          :limit-input-to-pattern="true"
          :helper-text="errors.zipCode || 'Please enter a five-digit ZIP code.'"
          @update:model-value="onZipCodeInput"
        />
      </div>
      <div class="flex flex-row-reverse">
        <RouteStepperNextButton :draft-data="draftData" :validate="validateFunc" @errors="errors = $event" />
      </div>
    </form>
  </Card>
</template>

<script lang="ts">
import { PropType } from 'vue';
import { HelperStatus } from '@/helpers/status';
import { geocode, type GeocodeResult, isValidZipcode } from '@/lib/geocode';
import TextField from '@/components/textfield/TextField.vue';
import Card from '@/components/card/Card.vue';
import RouteStepperNextButton from '@/components/route_stepper/RouteStepperNextButton.vue';
import { Location } from '@/store/modules/job/types';
import { CorporateOnboardingGuidedDraft } from './types';

interface Errors {
  zipCode?: string;
}

interface CorporateOnboardingGuidedLocationData {
  zipCode: string;
  location: Location | null;
  errors: Errors;
}

export default {
  name: 'CorporateOnboardingGuidedLocation',
  components: {
    Card,
    TextField,
    RouteStepperNextButton,
  },
  props: {
    draft: {
      type: Object as PropType<CorporateOnboardingGuidedDraft>,
      required: true,
    },
  },
  data(): CorporateOnboardingGuidedLocationData {
    const accountZipCode = this.$store.getters['account/getZipCode'] || '';
    const accountLocation = this.$store.getters['account/getLocation'] || null;

    return {
      zipCode: this.draft.zipCode || accountZipCode || '',
      location: {
        x: this.draft.location?.x || accountLocation?.x || null,
        y: this.draft.location?.y || accountLocation?.y || null,
      },
      errors: {},
    };
  },
  computed: {
    helperStatus(): HelperStatus {
      if (this.errors.zipCode) {
        return HelperStatus.Error;
      }
      return HelperStatus.Neutral;
    },
    draftData(): CorporateOnboardingGuidedDraft {
      return {
        zipCode: this.zipCode,
        location: this.location,
      };
    },
  },
  watch: {
    draft: {
      handler(draft: CorporateOnboardingGuidedDraft): void {
        if (draft.zipCode && draft.zipCode !== this.zipCode) {
          this.onZipCodeInput(draft.zipCode);
        }
        if (
          draft.location?.x != null &&
          draft.location?.y != null &&
          (this.location?.x !== draft.location.x || this.location?.y !== draft.location.y)
        ) {
          this.location = { x: draft.location.x, y: draft.location.y };
        }
      },
    },
  },
  methods: {
    resetValidation(): void {
      this.errors = {};
    },
    onZipCodeInput(zipCode: string): void {
      if (zipCode === this.zipCode) return;

      this.resetValidation();
      this.location = null;
      this.zipCode = zipCode;
    },
    async geocodeLocation(): Promise<GeocodeResult | null> {
      try {
        return await geocode(this.zipCode);
      } catch (error) {
        console.error('Error verifying location:', error);
        return null;
      }
    },
    async validateFunc(): Promise<Errors> {
      const errors: Errors = {};
      if (!isValidZipcode(this.zipCode)) {
        errors.zipCode = 'ZIP code is required. Enter a five digit ZIP code.';
        return errors;
      }
      const geocoded = await this.geocodeLocation();
      this.location = geocoded?.location ?? null;

      if (!this.location) {
        errors.zipCode = 'Unable to find location. Please check the ZIP code.';
        return errors;
      }

      return errors;
    },
  },
};
</script>

<style scoped lang="scss">
.form-stepper-card {
  @media (max-width: 480px) {
    background-color: #fbfbfb !important;
  }
}
</style>
